import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCheck, Loader2, CheckCircle, AlertCircle, ExternalLink, RefreshCw, Sparkles } from 'lucide-react';
import { bidExtractionsAPI, bidEvidenceAPI, BidExtraction, BidExtractionStatus, BidEvidenceType } from '../../services/bidService';

interface Props {
  sessionId: string;
  projectId?: string;
}

const STATUS_COPY: Partial<Record<BidExtractionStatus, { label: string; color: string }>> = {
  generating: { label: 'Analysing session…', color: 'text-amber-600' },
  'pending-review': { label: 'Ready to review', color: 'text-primary' },
  confirmed: { label: 'Confirmed', color: 'text-green-600' },
  dismissed: { label: 'Dismissed', color: 'text-slate-400' },
};

const EVIDENCE_LABELS: Record<BidEvidenceType, string> = {
  persona: 'persona',
  userStory: 'user story',
  nfr: 'NFR',
  adr: 'ADR',
  winTheme: 'win theme'
};

export const BidExtractTab: React.FC<Props> = ({ sessionId, projectId }) => {
  const navigate = useNavigate();
  const [extraction, setExtraction] = useState<BidExtraction | null | undefined>(undefined);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pulling, setPulling] = useState(false);
  const [pullCounts, setPullCounts] = useState<Record<BidEvidenceType, number> | null>(null);
  const [pullError, setPullError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await bidExtractionsAPI.get(projectId, sessionId);
      setExtraction(r.data.extraction);
    } catch (e: any) {
      if (e?.response?.status === 404) setExtraction(null);
    }
  }, [projectId, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while generating
  useEffect(() => {
    if (extraction?.status !== 'generating') return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [extraction?.status, load]);

  const trigger = async () => {
    if (!projectId) return;
    setTriggering(true);
    setError(null);
    try {
      await bidExtractionsAPI.trigger(projectId, sessionId, 'standard');
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.error ?? 'Failed to start extraction';
      setError(msg);
    } finally {
      setTriggering(false);
    }
  };

  const pullEvidence = async () => {
    if (!projectId) return;
    setPulling(true);
    setPullError(null);
    try {
      const r = await bidEvidenceAPI.pull(projectId, sessionId);
      setPullCounts(r.data.counts);
    } catch (e: any) {
      setPullError(e?.response?.data?.error ?? 'Failed to pull evidence');
    } finally {
      setPulling(false);
    }
  };

  if (!projectId) {
    return (
      <div className="p-6 flex flex-col items-center justify-center gap-3 text-center">
        <AlertCircle size={32} className="text-slate-300" />
        <p className="text-sm font-semibold text-navy">Session not linked to a project</p>
        <p className="text-xs text-slate-400">Associate this session with a bid-enabled project to extract requirements and evidence.</p>
      </div>
    );
  }

  if (extraction === undefined) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-slate-300" />
      </div>
    );
  }

  const status = extraction?.status;
  const canReExtract = status === 'confirmed' || status === 'dismissed';

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <FileCheck size={16} className="text-primary" />
        <span className="text-sm font-bold text-navy">Extract to Bid</span>
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        Analyse this session's transcript for bid-relevant requirements. Unlike REACH, bid sessions can be
        re-extracted as the discussion continues — only new content since the last run is processed.
      </p>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
          <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {!extraction && (
        <button
          onClick={trigger}
          disabled={triggering}
          className="flex items-center justify-center gap-2 rounded-xl h-10 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60"
        >
          {triggering
            ? <><Loader2 size={15} className="animate-spin" /> Queuing analysis…</>
            : <><FileCheck size={15} /> Run bid extraction</>
          }
        </button>
      )}

      {extraction && (
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#e2e8f0] bg-slate-50/50 flex items-center justify-between">
            <span className="text-xs font-bold text-navy uppercase tracking-wide">Extraction status</span>
            <button onClick={load} className="text-slate-400 hover:text-navy transition-colors">
              <RefreshCw size={13} />
            </button>
          </div>
          <div className="px-4 py-3 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {status === 'generating' && <Loader2 size={14} className="animate-spin text-amber-500" />}
              {status === 'pending-review' && <CheckCircle size={14} className="text-primary" />}
              {status === 'confirmed' && <CheckCircle size={14} className="text-green-500" />}
              {status === 'dismissed' && <AlertCircle size={14} className="text-slate-400" />}
              <span className={`text-sm font-semibold ${STATUS_COPY[status!]?.color ?? 'text-slate-500'}`}>
                {STATUS_COPY[status!]?.label ?? status}
              </span>
            </div>

            {extraction.generatedAt && (
              <p className="text-xs text-slate-400">
                Generated {new Date(extraction.generatedAt).toLocaleString()}
              </p>
            )}

            {extraction.draft && !extraction.draft.isEmpty && (
              <p className="text-xs text-slate-600 mt-1">
                {extraction.draft.requirements.length} requirement{extraction.draft.requirements.length !== 1 ? 's' : ''} found
              </p>
            )}

            {status === 'pending-review' && (
              <button
                onClick={() => navigate(`/projects/${projectId}/bid`)}
                className="mt-1 flex items-center gap-1.5 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark text-white text-xs font-bold shadow-sm transition-colors"
              >
                <ExternalLink size={13} /> Review and import
              </button>
            )}

            {status === 'confirmed' && (
              <button
                onClick={() => navigate(`/projects/${projectId}/bid`)}
                className="mt-1 flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 transition-colors"
              >
                <ExternalLink size={13} /> View compliance matrix
              </button>
            )}

            {canReExtract && (
              <button
                onClick={trigger}
                disabled={triggering}
                className="mt-1 flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60"
              >
                {triggering ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Re-extract
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#e2e8f0] bg-slate-50/50">
          <span className="text-xs font-bold text-navy uppercase tracking-wide">Pull evidence</span>
        </div>
        <div className="px-4 py-3 flex flex-col gap-2">
          <p className="text-xs text-slate-500 leading-relaxed">
            Copy personas, user stories, NFRs, ADRs, and win themes discovered in this session (or, for personas,
            anywhere in this project) into the bid module's evidence pool, so draft answers have real evidence to cite.
          </p>

          {pullError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
              <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{pullError}</p>
            </div>
          )}

          <button
            onClick={pullEvidence}
            disabled={pulling}
            className="flex items-center justify-center gap-2 rounded-xl h-9 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs font-bold shadow-sm transition-colors disabled:opacity-60"
          >
            {pulling ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {pulling ? 'Pulling evidence…' : 'Pull evidence'}
          </button>

          {pullCounts && (
            <p className="text-xs text-slate-600">
              {(Object.keys(pullCounts) as BidEvidenceType[])
                .map(type => `${pullCounts[type]} ${EVIDENCE_LABELS[type]}${pullCounts[type] !== 1 ? 's' : ''}`)
                .join(', ')} pulled
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
